import { User } from 'interfaces/user'
import {
  Card,
  Col,
  Button,
  Form,
  InputGroup,
  FormControl,
  Collapse,
  Dropdown,
  Row,
} from 'react-bootstrap'
import React, { useState, useEffect } from 'react'
import useUpdateUser from 'hooks/use-update-user-new'
import useUpdateUserMetaData from 'hooks/use-update-user-metadata'
import useUpdateUserResponseLimiting from 'hooks/use-update-user-response-limiting'
import toast from 'react-hot-toast'
import { Organization } from 'interfaces/organization'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import useUpdateAppleId from 'hooks/use-update-apple-id-details'

export type ResponseLimitingStrategy = 'no_limiting' | 'legacy' | 'panopticon'

const AdminToolsCardNew = ({
  user,
  primaryOrg,
  setShowPushTokensModal,
}: {
  user: User
  primaryOrg: Organization
  setShowPushTokensModal?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [maxPages, setMaxPages] = useState('')
  const [slackId, setSlackId] = useState<string>('')
  const [isAdmin, setIsAdmin] = useState(user?.is_admin)
  const [canHaveUnlimitedPages, setCanHaveUnlimitedPages] = useState(
    user?.can_have_unlimited_pages
  )
  const [currentResponseLimitingStrategy, setCurrentResponseLimitingStrategy] =
    useState<ResponseLimitingStrategy>('legacy')
  const updateUser = useUpdateUser(user?.uuid)
  const updateUserMetaData = useUpdateUserMetaData(user?.uuid, user?.id)
  const updateUserResponseLimiting = useUpdateUserResponseLimiting(
    user?.id,
    user?.uuid
  )
  const { data: appleId } = useGetAppleIdByUserId(user?.id)
  const updateAppleId = useUpdateAppleId()

  useEffect(() => {
    setMaxPages(user?.max_pages?.toString() || '')
  }, [user?.max_pages])

  useEffect(() => {
    if (user?.internal_slack_id) {
      setSlackId(user.internal_slack_id)
    }
  }, [user])

  useEffect(() => {
    setIsAdmin(user?.is_admin)
    setCanHaveUnlimitedPages(user?.can_have_unlimited_pages)
  }, [user?.is_admin])

  useEffect(() => {
    if (user?.response_limiting_strategy) {
      setCurrentResponseLimitingStrategy(user.response_limiting_strategy)
    }
  }, [user?.response_limiting_strategy])

  const handleUpdateUserToInternalAdmin = ({
    updatedField,
  }: {
    updatedField: boolean
  }) => {
    if (updatedField) {
      if (
        window.confirm(
          `Are you sure you want to make ${user.name} as LINQ INTERNAL ADMIN?`
        )
      ) {
        setIsAdmin(updatedField)
        updateUser.mutate(
          { linq_internal_admin: updatedField },
          {
            onSuccess: () => {
              toast('Update user to Linq internal admin', { icon: '👍' })
            },
            onError: () => {
              setIsAdmin(!updatedField) // Revert on error
            },
          }
        )
      }
    } else {
      setIsAdmin(updatedField)
      updateUser.mutate(
        { linq_internal_admin: updatedField },
        {
          onSuccess: () => {
            toast('Remove user from Linq internal admin', { icon: '👍' })
          },
          onError: () => {
            setIsAdmin(!updatedField) // Revert on error
          },
        }
      )
    }
  }

  const handleUpdateUserToHaveUnlimitedPage = ({
    updatedField,
  }: {
    updatedField: boolean
  }) => {
    setCanHaveUnlimitedPages(updatedField)
    updateUser.mutate(
      { can_have_unlimited_pages: updatedField },
      {
        onSuccess: () => {
          toast(`Successfully updated user's unlimited pages`, { icon: '👍' })
        },
        onError: () => {
          setCanHaveUnlimitedPages(!updatedField) // Revert on error
        },
      }
    )
  }

  const handleUpdateNumberOfAllowedPages = (e: React.FormEvent) => {
    e.preventDefault()
    if (maxPages) {
      updateUserMetaData.mutate(
        {
          max_pages_override: parseInt(maxPages),
        },
        {
          onSuccess: () => {
            toast(`Successfully updated user's max pages`, { icon: '👍' })
          },
        }
      )
    }
  }

  const handleUpdateSlackId = (e: React.FormEvent) => {
    e.preventDefault()
    if (slackId) {
      updateUser.mutate(
        {
          internal_slack_id: slackId,
        },
        {
          onSuccess: () => {
            toast(`Successfully updated Slack ID! 🎉`, { icon: '🎈' })
          },
        }
      )
    }
  }

  const handleResponseLimitingChange = (strategy: ResponseLimitingStrategy) => {
    // Optimistically update the local state
    setCurrentResponseLimitingStrategy(strategy)

    updateUserResponseLimiting.mutate(
      {
        response_limiting_strategy: strategy,
      },
      {
        onSuccess: () => {
          toast('Successfully updated response limiting strategy', {
            icon: '👍',
          })
        },
        onError: () => {
          // Revert on error
          setCurrentResponseLimitingStrategy(
            user?.response_limiting_strategy || 'legacy'
          )
          toast.error('Failed to update response limiting strategy')
        },
      }
    )
  }

  const getResponseLimitingDisplayText = (
    strategy: ResponseLimitingStrategy
  ) => {
    switch (strategy) {
      case 'no_limiting':
        return 'No Limiting'
      case 'legacy':
        return 'Legacy'
      case 'panopticon':
        return 'Panopticon'
      default:
        return 'Unknown'
    }
  }

  return (
    <Col md="12">
      <Card className="mt-3">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <h4 className="mb-0">Admin Tools</h4>
          <ion-icon
            name={isExpanded ? 'chevron-up-outline' : 'chevron-down-outline'}
            style={{
              fontSize: '20px',
              color: '#4da6ff',
              transition: 'transform 0.2s ease',
            }}
          />
        </Card.Header>

        <Collapse in={isExpanded}>
          <div>
            <Card.Body>
              {/* Response Limiting Strategy */}
              <div className="mb-2">
                <div
                  className="fw-bold mb-2"
                  style={{ fontSize: 'clamp(16px, 3.5vw, 18px)' }}
                >
                  Response Limiting Strategy
                </div>
                <Dropdown className="user-actions-dropdown">
                  <Dropdown.Toggle
                    as="div"
                    className="d-flex align-items-center"
                    bsPrefix="btn"
                    style={{
                      cursor: 'pointer',
                      fontSize: 'clamp(14px, 3vw, 16px)',
                      color: 'white',
                      backgroundColor: 'transparent',
                      border: 'none',
                    }}
                  >
                    <span className="me-2">
                      {getResponseLimitingDisplayText(
                        currentResponseLimitingStrategy
                      )}
                    </span>
                    <ion-icon
                      name="chevron-down-outline"
                      style={{
                        fontSize: '16px',
                        color: '#10B981',
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="bg-dark border-success border-2 rounded">
                    <Dropdown.Item
                      className="text-white px-3 py-2"
                      onClick={() =>
                        handleResponseLimitingChange('no_limiting')
                      }
                      disabled={updateUserResponseLimiting.isLoading}
                    >
                      <div>
                        <strong>No Limiting</strong>
                        <div className="text-muted small">
                          User can send unlimited messages
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item
                      className="text-white px-3 py-2"
                      onClick={() => handleResponseLimitingChange('legacy')}
                      disabled={updateUserResponseLimiting.isLoading}
                    >
                      <div>
                        <strong>Legacy</strong>
                        <div className="text-muted small">
                          Use existing response limiting decisions
                        </div>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Item
                      className="text-white px-3 py-2"
                      onClick={() => handleResponseLimitingChange('panopticon')}
                      disabled={updateUserResponseLimiting.isLoading}
                    >
                      <div>
                        <strong>Panopticon</strong>
                        <div className="text-muted small">
                          Use panopticon for response limiting decisions
                        </div>
                      </div>
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Internal Admin Toggle */}
              <div className="mb-2">
                <div
                  className="fw-bold mb-2"
                  style={{ fontSize: 'clamp(16px, 3.5vw, 18px)' }}
                >
                  Internal Admin
                </div>
                <Dropdown className="user-actions-dropdown">
                  <Dropdown.Toggle
                    as="div"
                    className="d-flex align-items-center"
                    bsPrefix="btn"
                    style={{
                      cursor: 'pointer',
                      fontSize: 'clamp(14px, 3vw, 16px)',
                      color: 'white',
                      backgroundColor: 'transparent',
                      border: 'none',
                    }}
                  >
                    <span className="me-2">{isAdmin ? 'Yes' : 'No'}</span>
                    <ion-icon
                      name="chevron-down-outline"
                      style={{
                        fontSize: '16px',
                        color: '#10B981',
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="bg-dark border-success border-2 rounded">
                    {isAdmin ? (
                      <Dropdown.Item
                        onClick={() =>
                          handleUpdateUserToInternalAdmin({
                            updatedField: false,
                          })
                        }
                      >
                        Remove Internal Admin
                      </Dropdown.Item>
                    ) : (
                      <Dropdown.Item
                        onClick={() =>
                          handleUpdateUserToInternalAdmin({
                            updatedField: true,
                          })
                        }
                      >
                        Make Internal Admin
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Unlimited Pages Toggle */}
              <div className="mb-2">
                <div
                  className="fw-bold mb-2"
                  style={{ fontSize: 'clamp(16px, 3.5vw, 18px)' }}
                >
                  Unlimited Pages
                </div>
                <Dropdown className="user-actions-dropdown">
                  <Dropdown.Toggle
                    as="div"
                    className="d-flex align-items-center"
                    bsPrefix="btn"
                    style={{
                      cursor: 'pointer',
                      fontSize: 'clamp(14px, 3vw, 16px)',
                      color: 'white',
                      backgroundColor: 'transparent',
                      border: 'none',
                    }}
                  >
                    <span className="me-2">
                      {canHaveUnlimitedPages ? 'Yes' : 'No'}
                    </span>
                    <ion-icon
                      name="chevron-down-outline"
                      style={{
                        fontSize: '16px',
                        color: '#10B981',
                      }}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="bg-dark border-success border-2 rounded">
                    {canHaveUnlimitedPages ? (
                      <Dropdown.Item
                        onClick={() =>
                          handleUpdateUserToHaveUnlimitedPage({
                            updatedField: false,
                          })
                        }
                      >
                        Disable Unlimited Pages
                      </Dropdown.Item>
                    ) : (
                      <Dropdown.Item
                        onClick={() =>
                          handleUpdateUserToHaveUnlimitedPage({
                            updatedField: true,
                          })
                        }
                      >
                        Enable Unlimited Pages
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Max Pages Override */}
              {!canHaveUnlimitedPages && (
                <div className="mb-2">
                  <div
                    className="fw-bold mb-2"
                    style={{ fontSize: 'clamp(16px, 3.5vw, 18px)' }}
                  >
                    Max Pages Override
                  </div>
                  <Form onSubmit={handleUpdateNumberOfAllowedPages}>
                    <div className="d-flex">
                      <FormControl
                        type="number"
                        value={maxPages}
                        onChange={(e) => setMaxPages(e.target.value)}
                        placeholder="Enter max pages"
                        style={{
                          fontSize: 'clamp(12px, 3vw, 14px)',
                          padding:
                            'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)',
                          backgroundColor: 'white',
                          border: 'none',
                          borderRadius: '5px 0 0 5px',
                          flex: 1,
                        }}
                      />
                      <Button
                        type="submit"
                        disabled={updateUserMetaData.isLoading}
                        style={{
                          fontSize: 'clamp(14px, 3vw, 16px)',
                          padding:
                            'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)',
                          backgroundColor: '#10B981',
                          border: 'none',
                          borderRadius: '0 5px 5px 0',
                          color: 'white',
                        }}
                      >
                        {updateUserMetaData.isLoading ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </Form>
                </div>
              )}

              {/* Internal Slack ID */}
              <div className="mb-3">
                <div
                  className="fw-bold mb-2"
                  style={{ fontSize: 'clamp(14px, 3vw, 16px)' }}
                >
                  Internal Slack ID
                </div>
                <Form onSubmit={handleUpdateSlackId}>
                  <InputGroup>
                    <FormControl
                      type="text"
                      value={slackId}
                      onChange={(e) => setSlackId(e.target.value)}
                      placeholder="Enter Slack ID"
                      style={{
                        fontSize: 'clamp(12px, 2.5vw, 14px)',
                        padding: 'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)',
                      }}
                    />
                    <Button
                      variant="outline-primary"
                      type="submit"
                      disabled={updateUser.isLoading}
                      style={{
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        padding: 'clamp(6px, 1.5vw, 8px) clamp(8px, 2vw, 12px)',
                        backgroundColor: '#10B981',
                        border: 'none',
                        borderRadius: '0 5px 5px 0',
                        color: 'white',
                      }}
                    >
                      {updateUser.isLoading ? 'Updating...' : 'Update'}
                    </Button>
                  </InputGroup>
                </Form>
              </div>

              {/* Push Tokens Button */}
              {setShowPushTokensModal && (
                <div className="mb-2">
                  <Button
                    onClick={() => setShowPushTokensModal(true)}
                    style={{
                      fontSize: 'clamp(16px, 3.5vw, 18px)',
                      //   padding: 'clamp(16px, 4vw, 20px) clamp(32px, 8vw, 48px)',
                      backgroundColor: '#10B981',
                      border: 'none',
                      borderRadius: '5px',
                      fontWeight: '500',
                      color: 'white',
                      width: '50%',
                    }}
                  >
                    Push Tokens
                  </Button>
                </div>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>
    </Col>
  )
}

export default AdminToolsCardNew
